import React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike';
import PoolIcon from '@mui/icons-material/Pool';
import HikingIcon from '@mui/icons-material/Hiking';
import ExploreIcon from '@mui/icons-material/Explore';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import SportsIcon from '@mui/icons-material/Sports';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import GroupsIcon from '@mui/icons-material/Groups';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import PersonIcon from '@mui/icons-material/Person';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import TivityLogo from './TivityLogo';
import { SPORT_TYPES } from '../constants/sportTypes';
import { useSportTypeLabel } from '../hooks/useSportTypeLabel';

const sportIcons: Record<(typeof SPORT_TYPES)[number], React.ReactElement> = {
    Run: <DirectionsRunIcon />,
    CO: <ExploreIcon />,
    'Marche nordique': <HikingIcon />,
    Velo: <DirectionsBikeIcon />,
    Natation: <PoolIcon />,
    Triathlon: <WorkspacePremiumIcon />,
    Autre: <SportsIcon />,
};

const featureKeys = ['trainings', 'races', 'admin'] as const;
const featureIcons: Record<(typeof featureKeys)[number], React.ReactElement> = {
    trainings: <DirectionsRunIcon color="primary" fontSize="large" />,
    races: <EmojiEventsIcon color="primary" fontSize="large" />,
    admin: <AdminPanelSettingsIcon color="primary" fontSize="large" />,
};

const stepKeys = ['step1', 'step2', 'step3'] as const;
const stepIcons: Record<(typeof stepKeys)[number], React.ReactElement> = {
    step1: <GroupsIcon fontSize="large" />,
    step2: <EventAvailableIcon fontSize="large" />,
    step3: <EmojiEventsIcon fontSize="large" />,
};

const audienceKeys = ['clubs', 'animators', 'members'] as const;
const audienceIcons: Record<(typeof audienceKeys)[number], React.ReactElement> = {
    clubs: <SupervisorAccountIcon fontSize="large" />,
    animators: <SportsIcon fontSize="large" />,
    members: <PersonIcon fontSize="large" />,
};

function Home() {
    const { t } = useTranslation();
    const sportTypeLabel = useSportTypeLabel();

    return (
        <Box>
            <Box
                sx={{
                    background: (theme) =>
                        `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 60%, ${theme.palette.secondary.main} 140%)`,
                    color: 'primary.contrastText',
                    py: { xs: 7, md: 12 },
                }}
            >
                <Container maxWidth="md" sx={{ textAlign: 'center' }}>
                    <Box display="flex" justifyContent="center" mb={2}>
                        <TivityLogo variant="light" size={56} />
                    </Box>
                    <Typography
                        sx={{
                            mb: 1.5,
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            letterSpacing: '0.14em',
                            textTransform: 'uppercase',
                            opacity: 0.75,
                        }}
                    >
                        {t('home.slogan')}
                    </Typography>
                    <Typography variant="h6" sx={{ mb: 1, opacity: 0.95, fontWeight: 400 }}>
                        {t('home.tagline')}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 4, opacity: 0.85 }}>
                        {t('home.subtagline')}
                    </Typography>
                    <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap" mb={4}>
                        <Button component={RouterLink} to="/login" variant="contained" color="button" size="large">
                            {t('home.ctaLogin')}
                        </Button>
                        <Button
                            component={RouterLink}
                            to="/signup"
                            variant="outlined"
                            color="inherit"
                            size="large"
                            sx={{ borderColor: 'rgba(255,255,255,0.7)' }}
                        >
                            {t('home.ctaSignup')}
                        </Button>
                    </Box>
                    <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
                        {SPORT_TYPES.filter((type) => type !== 'Autre').map((type) => (
                            <Chip
                                key={type}
                                icon={sportIcons[type]}
                                label={sportTypeLabel(type)}
                                size="small"
                                sx={{
                                    bgcolor: 'rgba(255,255,255,0.15)',
                                    color: 'inherit',
                                    '& .MuiChip-icon': { color: 'inherit' },
                                }}
                            />
                        ))}
                    </Stack>
                </Container>
            </Box>

            <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
                <Typography variant="h4" component="h2" textAlign="center" fontWeight={700} gutterBottom>
                    {t('home.howItWorksTitle')}
                </Typography>
                <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mb: 5 }}>
                    {t('home.howItWorksSubtitle')}
                </Typography>
                <Grid container spacing={4}>
                    {stepKeys.map((key, index) => (
                        <Grid item xs={12} md={4} key={key}>
                            <Box sx={{ textAlign: 'center', px: 2 }}>
                                <Avatar
                                    sx={{
                                        bgcolor: 'primary.main',
                                        color: 'primary.contrastText',
                                        width: 64,
                                        height: 64,
                                        mx: 'auto',
                                        mb: 2,
                                    }}
                                >
                                    {stepIcons[key]}
                                </Avatar>
                                <Typography variant="overline" color="text.secondary">
                                    {t('home.step', { number: index + 1 })}
                                </Typography>
                                <Typography variant="h6" gutterBottom>
                                    {t(`home.steps.${key}.title`)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t(`home.steps.${key}.description`)}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            <Container maxWidth="lg" sx={{ pb: { xs: 6, md: 8 } }}>
                <Typography variant="h4" component="h2" textAlign="center" fontWeight={700} gutterBottom>
                    {t('home.audiencesTitle')}
                </Typography>
                <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mb: 5 }}>
                    {t('home.audiencesSubtitle')}
                </Typography>
                <Grid container spacing={4} justifyContent="center">
                    {audienceKeys.map((key) => (
                        <Grid item xs={12} sm={6} md={4} key={key}>
                            <Card sx={{ height: '100%', p: 3, textAlign: 'center' }}>
                                <Avatar
                                    sx={{
                                        bgcolor: 'primary.main',
                                        color: 'primary.contrastText',
                                        width: 64,
                                        height: 64,
                                        mx: 'auto',
                                        mb: 2,
                                    }}
                                >
                                    {audienceIcons[key]}
                                </Avatar>
                                <Typography variant="h6" gutterBottom>
                                    {t(`home.audiences.${key}.title`)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t(`home.audiences.${key}.description`)}
                                </Typography>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            <Box sx={{ bgcolor: 'grey.50', py: { xs: 6, md: 8 } }}>
                <Container maxWidth="lg">
                    <Grid container spacing={4}>
                        {featureKeys.map((key) => (
                            <Grid item xs={12} md={4} key={key}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        textAlign: 'center',
                                        p: 2,
                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                        '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 },
                                    }}
                                >
                                    <CardContent>
                                        <Avatar
                                            sx={{
                                                bgcolor: 'icon.main',
                                                width: 64,
                                                height: 64,
                                                mx: 'auto',
                                                mb: 2,
                                            }}
                                        >
                                            {featureIcons[key]}
                                        </Avatar>
                                        <Typography variant="h6" gutterBottom>
                                            {t(`home.features.${key}.title`)}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {t(`home.features.${key}.description`)}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            <Box sx={{ bgcolor: 'primary.dark', color: 'primary.contrastText', py: { xs: 6, md: 8 } }}>
                <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" component="h2" fontWeight={700} gutterBottom>
                        {t('home.closingTitle')}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 4, opacity: 0.9 }}>
                        {t('home.closingSubtitle')}
                    </Typography>
                    <Button component={RouterLink} to="/register-club" variant="contained" color="button" size="large">
                        {t('home.closingCta')}
                    </Button>
                </Container>
            </Box>
        </Box>
    );
}

export default Home;
